import React, { useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { PanelWithCheck } from 'apps/auth/components/common/components/PanelWithCheck/PanelWithCheck';
import { EmptyPage } from 'apps/auth/components/common/Layout/EmptyPage/EmptyPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { CITY, POST_CODE } from 'apps/auth/constants/fieldsName';
import { resolveArguments, timer } from 'apps/auth/helpers/utils';
import { useFields, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import { AutoAddressCompleteAddressService } from '../utils/AutoAddressCompleteAddressService';
import { SearchBar } from './components/SearchBar/SearchBar';

const InputAutoCompleteAddressList = ({ countryCode, searchPlaceholder, formName, selectedAddress, items, field, ...props }) => {
    const { textNotFound, textInfoNotFound, buttonNotFound } = usePageMessages();
    const { lang } = useLanguage();
    const { post_code, city } = useFields(formName);
    const searchRef = useRef(null);
    const { current: timerId } = useRef(null);
    const controllerRef = useRef(null);
    const { current: getAddressService } = useRef(new AutoAddressCompleteAddressService(formName, lang, props.setItems, props.selectItem, controllerRef));

    const [itemsList, setItems] = useState(items);
    const [query, setQuery] = useState(field.value);

    const [selectItem, searchWithApi] = useMemo(() => {
        getAddressService.setCountryCode(countryCode);
        return getAddressService.suggestionApiSearch(searchRef, setItems);
    }, [post_code?.isError, city?.isError]);

    useEffect(() => {
        timer(timerId, () => searchWithApi(query), 500);
    }, [query]);

    const onChange = (e) => {
        e.preventDefault();
        setQuery(e.target.value);
    };

    const setCurrentValue = (e) => {
        e.persist();
        props.selectInputClient(e, query.trim());
    };

    const selectNewItem = (e, item) => {
        e.persist();
        if (city?.isError) {
            props.removeFieldError(formName, CITY);
        }
        if (post_code?.isError) {
            props.removeFieldError(formName, POST_CODE);
        }
        selectItem(e, item);
    };

    const notFoundBlock = (addressText) => {
        const text = itemsList.length > 0 ? textInfoNotFound : textNotFound;
        return resolveArguments(text, addressText);
    };

    return (
        <>
            <SearchBar
                placeholder={searchPlaceholder}
                onChange={onChange}
                id={`search_field_${field.name}_${field.id}`}
                onCloseButtonClick={props.onCloseButtonClick}
                searchRef={searchRef}
                value={query}
                field={field}
                formName={formName}
            />
            <Row indent="adaptive-32-48">
                {itemsList.length > 0 &&
                    itemsList.map((item, index) => {
                        const { id, mainText, secondaryText } = getAddressService.serializeItem(item);
                        return (
                            <PanelWithCheck
                                key={`address_list_${index}`}
                                onClick={(e) => selectNewItem(e, item)}
                                mainText={mainText}
                                secondaryText={secondaryText}
                                id={id}
                                selectedPanelId={selectedAddress.id}
                                dataTestId={`autocomplete-address-${index}`}
                            />
                        );
                    })}
            </Row>
            {(query || itemsList.length > 0) && (
                <EmptyPage
                    iconName={itemsList.length === 0 ? 'ghost' : ''}
                    emptyText={notFoundBlock(query)}
                    emptyButtonClick={setCurrentValue}
                    emptyButtonCaption={buttonNotFound}
                />
            )}
        </>
    );
};

InputAutoCompleteAddressList.propTypes = {
    countryCode: PropTypes.string.isRequired,
    selectItem: PropTypes.func.isRequired,
    selectInputClient: PropTypes.func.isRequired,
    items: PropTypes.array.isRequired,
    setItems: PropTypes.func.isRequired,
    formName: PropTypes.string.isRequired,
    field: PropTypes.object.isRequired,
    selectedAddress: PropTypes.object.isRequired,
    setSelectedAddress: PropTypes.func.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
    searchDropBoxEmpty: PropTypes.string.isRequired,
    onCloseButtonClick: PropTypes.func.isRequired,
};

export default InputAutoCompleteAddressList;
